import React, { useState, useEffect } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import apiClient from './api/client';
import { Edit2, Save, X, Eye, EyeOff } from 'lucide-react';
import { THEME } from './theme/themeColors';

const UserInfo = () => {
  const [userData, setUserData] = useState({
    userId: '',
    username: '',
    email: '',
    role: '',
    domain: '',
    userRoleId: ''
  });

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ username: '', email: '' });
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = () => {
    const profile = {
      userId: sessionStorage.getItem('userId') || '',
      username: sessionStorage.getItem('username') || '',
      email: sessionStorage.getItem('userEmail') || '',
      role: sessionStorage.getItem('role')?.toUpperCase() || '',
      domain: sessionStorage.getItem('selectedDomain') || '',
      userRoleId: sessionStorage.getItem('userRoleId') || ''
    };

    setUserData(profile);
    setFormData({ username: profile.username, email: profile.email });
  };

  const handleProfileUpdate = async () => {
    if (!formData.username?.trim()) return toast.error("Username is required");
    if (!formData.email?.trim()) return toast.error("Email is required");

    setUpdatingProfile(true);

    const payload = {
      userId: Number(userData.userId),
      szUserName: formData.username.trim(),
      szEmail: formData.email.trim(),
      userRoleId: Number(userData.userRoleId),
      szDomainName: userData.domain
    };

    console.log("Sending Payload:", payload);

    try {
      await apiClient.post('/api/adminusers/update', payload);

      sessionStorage.setItem('username', formData.username.trim());
      sessionStorage.setItem('userEmail', formData.email.trim());

      setUserData(prev => ({
        ...prev,
        username: formData.username.trim(),
        email: formData.email.trim()
      }));

      setIsEditing(false);
      
      toast.success("User updated successfully!");

      window.dispatchEvent(new Event('userProfileUpdated'));

    } catch (err) {
      console.error("Update Error:", err.response?.data || err);
      toast.error(err.response?.data?.message || "Update failed");
    } finally {
      setUpdatingProfile(false);
    }
  };

    const handlePasswordChange = async () => {
    const { oldPassword, newPassword, confirmPassword } = passwordForm;

    if (!oldPassword || !newPassword || !confirmPassword) {
      return toast.error("All password fields are required");
    }
    if (newPassword !== confirmPassword) {
      return toast.error("New passwords do not match");
    }
    if (oldPassword === newPassword) {
      return toast.error("New password cannot be the same as old password");
    }

    const domain = sessionStorage.getItem('selectedDomain') || 'greenyourbills.com';
    const userId = sessionStorage.getItem('userId');

    if (!userId) {
      return toast.error("User ID not found. Please login again.");
    }

    setChangingPassword(true);

    try {
      const payload = {
        userId,
        oldPassword,
        newPassword,
        domain
      };

      const response = await apiClient.post('/api/admin/change-password', payload);

      if (response.data.success) {
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordSection(false);
        toast.success("Password updated successfully!");
      } else {
        toast.error(response.data.message || "Password change failed");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Password change failed";
      toast.error(errorMsg);
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <>
      <Toaster position="top-right" />

      <div className="card shadow-sm border-0">
        <div className="card-body p-4">
          <div className="row g-4">
            {/* Profile Details */}
            <div className="col-12 col-lg-6">
              <div style={{ background: '#f8f9fa', padding: '18px', borderRadius: '10px' }}>
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <h5 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>Profile Details</h5>
                  {!isEditing ? (
                    <button 
                      className="btn btn-sm d-flex align-items-center gap-1" 
                      style={{ color: THEME.primaryHover, padding: '4px 10px' }} 
                      onClick={() => setIsEditing(true)}
                    >
                      <Edit2 size={15} /> Edit
                    </button>
                  ) : (
                    <div className="d-flex gap-2">
                      <button 
                        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" 
                        onClick={() => { 
                          setIsEditing(false); 
                          setFormData({ username: userData.username, email: userData.email }); 
                        }}
                      >
                        <X size={15} /> Cancel
                      </button>
                      <button 
                        className="btn btn-sm d-flex align-items-center gap-1" 
                        style={{ backgroundColor: THEME.primary, color: THEME.onPrimary }}
                        onClick={handleProfileUpdate}
                        disabled={updatingProfile}
                      >
                        <Save size={15} /> {updatingProfile ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="row g-3">
                  <div className="col-12">
                    <label className="filter-label">Username <span className="text-danger">*</span></label>
                    {isEditing ? (
                      <input 
                        type="text" 
                        className="form-control form-control-xs" 
                        value={formData.username} 
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      />
                    ) : (
                      <div className="form-control form-control-xs bg-white" style={{ padding: '7px 12px' }}>
                        {userData.username || '—'}
                      </div>
                    )}
                  </div>

                  <div className="col-12">
                    <label className="filter-label">Email ID <span className="text-danger">*</span></label>
                    {isEditing ? (
                      <input 
                        type="email" 
                        className="form-control form-control-xs" 
                        value={formData.email} 
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    ) : (
                      <div className="form-control form-control-xs bg-white" style={{ padding: '7px 12px' }}>
                        {userData.email || '—'}
                      </div>
                    )}
                  </div>

                  <div className="col-12">
                    <label className="filter-label">Role</label>
                    <div className="form-control form-control-xs bg-light" style={{ padding: '7px 12px', color: '#666' }}>
                      {userData.role || '—'}
                    </div>
                  </div>

                  <div className="col-12">
                    <label className="filter-label">Domain</label>
                    <div className="form-control form-control-xs bg-light" style={{ padding: '7px 12px', color: '#666' }}>
                      {userData.domain || '—'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Change Password */}
            <div className="col-12 col-lg-6">
              <div style={{ background: '#f8f9fa', padding: '18px', borderRadius: '10px' }}>
                <h5 style={{ marginBottom: '16px', fontSize: '15px', fontWeight: 600 }}>
                  Change Password
                </h5>

                {!showPasswordSection ? (
                  <button 
                    className="btn px-4 py-2" 
                    style={{ backgroundColor: THEME.primary, color: THEME.onPrimary, borderRadius: '6px' }}
                    onClick={() => setShowPasswordSection(true)}
                  >
                    Change Password
                  </button>
                ) : (
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="filter-label">Old Password</label>
                      <div className="position-relative">
                        <input 
                          type={showOldPassword ? "text" : "password"} 
                          className="form-control form-control-xs" 
                          value={passwordForm.oldPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                          placeholder="Enter old password"
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-2 p-0"
                          style={{ color: '#6c757d', zIndex: 10 }}
                          onClick={() => setShowOldPassword(!showOldPassword)}
                        >
                          {showOldPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                        </button>
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="filter-label">New Password</label>
                      <input 
                        type="password" 
                        className="form-control form-control-xs" 
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                        placeholder="Enter new password"
                      />
                    </div>
                    <div className="col-12">
                      <label className="filter-label">Confirm New Password</label>
                      <div className="position-relative">
                        <input 
                          type={showConfirmPassword ? "text" : "password"} 
                          className="form-control form-control-xs" 
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                          placeholder="Re-enter new password"
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-2 p-0"
                          style={{ color: '#6c757d', zIndex: 10 }}
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                        </button>
                      </div>
                    </div>

                    <div className="col-12 d-flex gap-2 mt-2">
                      <button 
                        className="btn btn-sm btn-outline-secondary flex-fill py-2"
                        onClick={() => {
                          setShowPasswordSection(false);
                          setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
                          setShowOldPassword(false);
                          setShowConfirmPassword(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button 
                        className="btn btn-sm flex-fill py-2" 
                        style={{ backgroundColor: THEME.primary, color: THEME.onPrimary }}
                        onClick={handlePasswordChange}
                        disabled={changingPassword}
                      >
                        {changingPassword ? 'Updating...' : 'Update Password'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserInfo;